import { useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import Button from '@/V2/components/ui/button/button';
import { FormField } from '@/V2/components/ui/form/form-field';
import { TextArea } from '@/V2/components/ui/form/textarea';
import { useBrandStory } from '@/V2/features/brand-strategy/hooks/use-brand-story';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useUpdateGettingToKnowYou } from '../../hooks/use-update-getting-to-know-you';
import { useBrandStrategyStore } from '../../store/brand-strategy-store';

const formSchema = z.object({
  why_start_the_business: z.string(),
  unique_industry_perspective: z.string(),
  proudest_moment_in_business: z.string(),
  purpose: z.string(),
  mission: z.string(),
  vision: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

interface GettingToKnowYouFormProps {
  projectId: string;
}

export const GettingToKnowYouForm = ({
  projectId,
}: GettingToKnowYouFormProps) => {
  const { data: brandStoryData, isLoading } = useBrandStory(projectId);
  const updateGettingToKnowYou = useUpdateGettingToKnowYou(projectId);
  const { brandStrategyId, brandStrategy } = useBrandStrategyStore();

  // Create stable form data that only changes when brand story data actually changes
  const stableFormData = useMemo(() => {
    if (brandStoryData?.data) {
      return {
        why_start_the_business:
          brandStoryData.data.why_start_the_business || '',
        unique_industry_perspective:
          brandStoryData.data.unique_industry_perspective || '',
        proudest_moment_in_business:
          brandStoryData.data.proudest_moment_in_business || '',
        purpose: brandStoryData.data.purpose || '',
        mission: brandStoryData.data.mission || '',
        vision: brandStoryData.data.vision || '',
      };
    }

    // Default form data
    return {
      why_start_the_business: '',
      unique_industry_perspective: '',
      proudest_moment_in_business: '',
      purpose: '',
      mission: '',
      vision: '',
    };
  }, [brandStoryData?.data]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: stableFormData,
  });

  // Track previous data to handle form synchronization
  const prevDataRef = useRef(stableFormData);

  // Handle form resets when data changes (without useEffect)
  if (JSON.stringify(prevDataRef.current) !== JSON.stringify(stableFormData)) {
    form.reset(stableFormData);
    prevDataRef.current = stableFormData;
  }

  const handleSubmit = async (values: FormValues) => {
    if (!brandStrategyId || !brandStrategy) {
      console.error('Brand strategy ID or data is missing');
      toast.error('Failed to update getting to know you');
      return;
    }

    try {
      await updateGettingToKnowYou.mutateAsync(values);
    } catch (error) {
      console.error('Error updating getting to know you:', error);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className='flex justify-between items-center mb-6'>
        <div className='flex flex-col'>
          <h2 className='text-xl font-semibold mb-1'>Getting to Know You</h2>
          <p className='text-sm'></p>
        </div>
        <div className='flex mt-2 mr-4'>
          <Button
            type='submit'
            variant='primary'
            size='xsmall'
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit(handleSubmit)(e);
            }}
            isLoading={updateGettingToKnowYou.isPending}
            isDisabled={updateGettingToKnowYou.isPending || !brandStrategyId}
          >
            Save
          </Button>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit(handleSubmit)(e);
        }}
        className='space-y-6 mr-4'
      >
        <div className='space-y-4'>
          <FormField
            label='Why did you start the business?'
            error={form.formState.errors.why_start_the_business?.message}
          >
            <Controller
              name='why_start_the_business'
              control={form.control}
              render={({ field }) => (
                <TextArea
                  placeholder='...'
                  className='min-h-[80px] max-h-[300px] resize-y'
                  {...field}
                  value={field.value || ''}
                />
              )}
            />
          </FormField>

          <FormField
            label='What unique perspective do you bring to the industry?'
            error={form.formState.errors.unique_industry_perspective?.message}
          >
            <Controller
              name='unique_industry_perspective'
              control={form.control}
              render={({ field }) => (
                <TextArea
                  placeholder='...'
                  className='min-h-[80px] max-h-[300px] resize-y'
                  {...field}
                  value={field.value || ''}
                />
              )}
            />
          </FormField>

          <FormField
            label='What has been your proudest moment in business?'
            error={form.formState.errors.proudest_moment_in_business?.message}
          >
            <Controller
              name='proudest_moment_in_business'
              control={form.control}
              render={({ field }) => (
                <TextArea
                  placeholder='...'
                  className='min-h-[80px] max-h-[300px] resize-y'
                  {...field}
                  value={field.value || ''}
                />
              )}
            />
          </FormField>

          <FormField
            label='What is your purpose?'
            error={form.formState.errors.purpose?.message}
          >
            <Controller
              name='purpose'
              control={form.control}
              render={({ field }) => (
                <TextArea
                  placeholder='...'
                  className='min-h-[80px] max-h-[300px] resize-y'
                  {...field}
                  value={field.value || ''}
                />
              )}
            />
          </FormField>

          <FormField
            label='What is your mission?'
            error={form.formState.errors.mission?.message}
          >
            <Controller
              name='mission'
              control={form.control}
              render={({ field }) => (
                <TextArea
                  placeholder='...'
                  className='min-h-[80px] max-h-[300px] resize-y'
                  {...field}
                  value={field.value || ''}
                />
              )}
            />
          </FormField>

          <FormField
            label='What is your vision?'
            error={form.formState.errors.vision?.message}
          >
            <Controller
              name='vision'
              control={form.control}
              render={({ field }) => (
                <TextArea
                  placeholder='...'
                  className='min-h-[80px] max-h-[300px] resize-y'
                  {...field}
                  value={field.value || ''}
                />
              )}
            />
          </FormField>
        </div>
      </form>
    </div>
  );
};
